import {
  JsonapiEntity,
  ManyUnresolvedIdentifiersOr,
  attribute,
  entity,
  link,
  meta,
  relationship,
} from '@talisdev/jsonapi-transformers';

import { HierarchyNode } from '®shared/hierarchy/common';
import { License, ListAccess, Period, Tenant } from '®shared/tenant';
import { UserRecord } from '®shared/user/user-record';

import { ListItem } from './list-item';
import { ListPublishAction, PublishActionStatus } from './list-publish-action';
import { Section } from './section';

export enum PublishedStatus {
  archived = 'archived',
  draft = 'draft',
  published = 'published',
}

type ManyUnresolvedIdentifiersOrSectionOrItem = ManyUnresolvedIdentifiersOr<
  Section | ListItem
>;

@entity({ type: 'lists' })
export class ReadingListDetails extends JsonapiEntity<ReadingListDetails> {
  @attribute() title: string;
  @attribute() default_view?: string;
  @attribute() description?: string;
  @attribute() date_created?: string;
  @attribute() list_access?: ListAccess | null;
  @attribute() last_published?: string;
  @attribute() last_updated?: string;
  @attribute() published_status?: PublishedStatus;
  @attribute() student_numbers?: number;
  @attribute() internal_note?: string;

  // related content
  @relationship() license?: License;
  @relationship({ name: 'nodes' })
  all_nodes?: HierarchyNode[];
  /**
   * `nodes` ALWAYS returns a HierarchyNode[], for downstream ease.
   *
   * The API property is called `nodes`, so we use jsonapi-transformer aliasing to
   * rename the class property to `all_nodes`, before wrapping in this getter method.
   *
   * @return {HierarchyNode[]} An array of nodes, always defined (but possibly empty)
   */
  get nodes(): HierarchyNode[] {
    return this.all_nodes || [];
  }
  @relationship() period?: Period;
  @relationship() owners?: UserRecord[];
  @relationship() rolled_over_to?: ReadingListDetails;
  @relationship() rolled_over_from?: ReadingListDetails;
  @relationship({ name: 'list_publish_action' })
  listPublishAction?: ListPublishAction;

  // structural content
  @relationship({ allowUnresolvedIdentifiers: true })
  all_children?: ManyUnresolvedIdentifiersOrSectionOrItem;

  /**
   * We alias `sections` so we can ensure that it returns an array
   */
  @relationship({ name: 'sections' })
  legacySections?: Section[];

  get sections(): Section[] {
    if (!this.all_children) {
      return [];
    }
    return this.all_children
      .filter(child => child.type === 'sections')
      .map(section => section as Section);
  }

  @relationship() tenant: Tenant;

  @link({ name: 'review' })
  activeReviewUrl?: string;

  @meta() item_count?: number;
  @meta() list_etag?: string;
  @meta({ name: 'total_student_numbers' })
  totalStudentNumbers?: number;
  @meta({ name: 'has_unpublished_changes' })
  hasUnpublishedChanges?: boolean;

  get is_published(): boolean {
    return this.published_status === PublishedStatus.published;
  }

  get is_draft(): boolean {
    return this.published_status === PublishedStatus.draft;
  }

  get is_archived(): boolean {
    return this.published_status === PublishedStatus.archived;
  }

  get isPublishing(): boolean {
    if (this.listPublishAction) {
      return this.listPublishAction.status === PublishActionStatus.PROCESSING;
    }
    return false;
  }
}
